import React, { useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useFormContext } from 'react-hook-form';

import FormItem from "app/components/inputs/FormItem";
import TimeRangeInput from "app/components/inputs/TimeRangeInput";
import DateSelector from "./DateSelector";
import RecurrenceSelector from "./RecurrenceSelector";
import SchedulingUserSelector from "app/components/SchedulingUserSelector";
import { UserContext, RoleType } from 'app/contexts/UserContext';
import { CoachProfileModel } from "app/models/scheduling/CoachProfileModel";
import { errorMessageFor } from './helpers';

import styles from './styles.module.scss';
import _ from 'lodash';
import TextInput from 'app/components/inputs/TextInput';
import SwitchToggle from 'app/components/OptionButtonGroup/SwitchToggle';
import useSlotInterval from 'app/utils/hooks/scheduling/useSlotInterval';
import useCalendarPreferences from 'app/utils/hooks/useCalendarPreferences';
import useFacilityResources from 'app/routes/Calendar/views/FacilityCalendarView/hooks/useFacilityResources';
import { activeBaysInAdminOrder } from 'app/utils/request/scheduling/facilityResources';
import EventTypePill from './EventTypeSelector/EventTypePill';
import ResourceMultiSelect from './ResourceMultiSelect';
import { BLOCK_FOR_COACHES, BLOCK_FOR_RESOURCES, BlockOwnerMode, showsBlockOwner } from './blockOwner';

import ContactIcon from "../../../../public/images/contact-icon.svg";
import CalendarIcon from "../../../../public/images/calendar-ico.svg";
import EditPenIcon from "../../../../public/images/edit-pen.svg";
import LocationIcon from "../../../../public/images/location-dark.svg";

type PropsDef = {
  onDateChange: (_date: Date) => void;
  showCoachSelection?: boolean;
  onCoachChange?: (_e: any) => void;
  loadCoaches?: (_inputValue: string, _currentOptions: [], _additional: { page: number }) => Promise<any>;
  validateCoachSelection?: (_selection: CoachProfileModel[], _formValues: any) => boolean;
  // The facility calendar only. True when the user can block a bay (an admin,
  // a coach-admin or a scheduler, with the feature on). The form then shows
  // "Applies to".
  allowResourceBlock?: boolean;
  // True on an edit. A block keeps its owner, so "Applies to" and the bays
  // do not change.
  lockOwner?: boolean;
  // True for a scheduler. "Applies to" stays on "Resources", and the bays
  // change.
  resourcesOnly?: boolean;
  facilityId?: string;
  // The bays of the block, for a bay that the list does not offer.
  heldResources?: { id: number; title: string }[];
  validateResourceSelection?: (_ids: number[], _formValues: any) => boolean;
  onAppliesToChange?: (_mode: BlockOwnerMode) => void;
};

export default function ScheduleBlockForm(props: PropsDef) {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const {
    onDateChange,
    showCoachSelection,
    onCoachChange,
    loadCoaches,
    validateCoachSelection,
    allowResourceBlock = false,
    lockOwner = false,
    resourcesOnly = false,
    facilityId,
    heldResources,
    validateResourceSelection,
    onAppliesToChange,
  } = props;

  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const watchTimeZone = watch('timeZone');
  const watchStartTime = watch('start');
  const watchEndTime = watch('end');
  const watchCoaches = watch('coachProfiles');
  const watchAppliesTo = watch('appliesTo');

  // The owner choice shows on a new block, and on the edit of a bay block
  // only. The bays of the facility, in the order of the Resources page, load
  // only when the choice shows.
  const ownerChoice = showsBlockOwner(allowResourceBlock, lockOwner, watchAppliesTo);
  const { resources: facilityResources } = useFacilityResources(
    facilityId,
    ownerChoice,
  );
  const activeBays = useMemo(
    () => activeBaysInAdminOrder(facilityResources),
    [facilityResources],
  );
  const blocksResources = allowResourceBlock && watchAppliesTo === BLOCK_FOR_RESOURCES;
  // The choice shows while the facility has a bay. A block that already
  // belongs to a bay keeps the choice, also when its bay is off now.
  const showAppliesTo = ownerChoice && (activeBays.length > 0 || blocksResources);

  const luxonStartAt = useMemo(() => {
    return DateTime.fromISO(watchStartTime, { zone: watchTimeZone });
  }, [watchStartTime]);

  const luxonEndAt = useMemo(() => {
    return DateTime.fromISO(watchEndTime, { zone: watchTimeZone });
  }, [watchEndTime]);

  // A bay block is never private.
  const allowPrivateBlock = !blocksResources &&
                            watchCoaches?.length === 1 &&
                            watchCoaches[0].id === user.id &&
                            user.role === RoleType.COACH;

  const { step } = useSlotInterval();

  const onStartTimeChange = (changeEvent: any) => {
    const newStartAt = DateTime.fromISO(changeEvent.target.value, { zone: watchTimeZone });
    const diff =  newStartAt.diff(luxonStartAt);

    setValue('start', changeEvent.target.value);
    if (luxonEndAt.isValid) {
      setValue('end', luxonEndAt.plus(diff));
    }
  };

  const onEndTimeChange = (changeEvent: any) => {
    const newEndAt = DateTime.fromISO(changeEvent.target.value, { zone: watchTimeZone });

    if (newEndAt > luxonStartAt) {
      setValue('end', changeEvent.target.value);
      return;
    }

    setValue('end', luxonStartAt.plus({ minutes: step }).toISO());
  };

  const { preferences } = useCalendarPreferences();

  const calculateHours = (step: number, timeFormat: "12h" | "24h") => {
    const dateList: { value: number; label: string }[] = [];

    _.range(0, 24 * 60, step).forEach((value) => {
      const hour = Math.floor(value / 60);
      const minute = value % 60;

      let label: string;
      if (timeFormat === "24h") {
        label = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      } else {
        const ampm = hour >= 12 ? "PM" : "AM";
        if (hour === 0) {
          label = `12:${minute.toString().padStart(2, "0")} ${ampm}`;
        } else if (hour > 12) {
          label = `${hour - 12}:${minute.toString().padStart(2, "0")} ${ampm}`;
        } else {
          label = `${hour}:${minute.toString().padStart(2, "0")} ${ampm}`;
        }
      }

      dateList.push({ value, label });
    });

    return dateList;
  };

  const dateList = calculateHours(step, preferences.timeFormat);

  return (
    <div className={styles.two_column_layout}>
      {/* Applies to: coaches or bays (FacilityCalendar only) */}
      {showAppliesTo && (
        <div className={styles.applies_to_row} data-testid="block-applies-to">
          <span className={styles.applies_to_label}>{t('Applies to')}</span>
          {[
            { mode: BLOCK_FOR_COACHES, label: t('Coaches') },
            { mode: BLOCK_FOR_RESOURCES, label: t('Resources') },
          ].map(({ mode, label }) => (
            <EventTypePill
              key={mode}
              label={label}
              isActive={(watchAppliesTo || BLOCK_FOR_COACHES) === mode}
              onClick={() => onAppliesToChange?.(mode)}
              disabled={lockOwner || resourcesOnly}
              data-testid={`block-applies-to-${mode}`}
            />
          ))}
        </div>
      )}

      {/* Bay selector (FacilityCalendar only) */}
      {blocksResources && (
        <div className={styles.icon_row}>
          <img src={LocationIcon} alt="" className={styles.row_icon} />
          <div className={styles.row_content}>
            <FormItem
              // The service names a bad bay (off, a location, another
              // facility) on `schedule_resource_id`, as for a lesson.
              error={
                errorMessageFor(errors, 'scheduleResourceIds') ??
                errorMessageFor(errors, 'scheduleResourceId')
              }
              className={styles.input_field}
            >
              <label className={styles.sr_only}>{t('Resources')}</label>
              <ResourceMultiSelect
                name="scheduleResourceIds"
                bays={activeBays}
                heldResources={heldResources}
                rules={{ validate: validateResourceSelection }}
                disabled={lockOwner}
              />
            </FormItem>
          </div>
        </div>
      )}

      {/* Coach selector (FacilityCalendar only) */}
      {showCoachSelection && !blocksResources && (
        <div className={styles.icon_row}>
          <img src={ContactIcon} alt="" className={styles.row_icon} />
          <div className={styles.row_content}>
            <FormItem
              error={errorMessageFor(errors, 'coachProfiles')}
              className={styles.input_field}
            >
              <label className={styles.sr_only}>{t('Coach')}</label>
              <SchedulingUserSelector
                {...register('coachProfiles', {
                  onChange: onCoachChange,
                  validate: validateCoachSelection,
                })}
                control={control}
                loadOptions={loadCoaches}
                placeholder={t('Select Coach')}
                multiSelect
                allowMultiSelectAll
                allOptionsLabel={t('All Coaches')}
                canAddNewAthletes={false}
              />
            </FormItem>
          </div>
        </div>
      )}

      {/* Title row */}
      <div className={styles.icon_row}>
        <img src={EditPenIcon} alt="" className={styles.row_icon} />
        <div className={styles.row_content}>
          <FormItem error={errorMessageFor(errors, 'title')} className={styles.input_field}>
            <label className={styles.sr_only}>{t('Title')}</label>
            <TextInput placeholder={t('Event Name')} {...register('title')} />
          </FormItem>
        </div>
      </div>

      {/* Private toggle */}
      {allowPrivateBlock && (
        <div style={{ marginLeft: 36 }}>
          <FormItem error={errorMessageFor(errors, 'private')} className={`${styles.input_field} ${styles.checkbox_field}`}>
            <SwitchToggle
              checked={watch('private') || false}
              onChange={(checked) => {
                setValue('private', checked);
              }}
              title={t('Private')}
              small
              data-testid="private-toggle"
            />
          </FormItem>
        </div>
      )}

      {/* Date / Time / Recurrence row */}
      <div className={styles.icon_row}>
        <img src={CalendarIcon} alt="" className={styles.row_icon} />
        <div className={styles.row_content}>
          <div className={styles.date_time_row}>
            <FormItem error={errorMessageFor(errors, 'date')} className={styles.input_field}>
              <label className={styles.sr_only}>{t('Date')}</label>
              <DateSelector {...register('date', { onChange: onDateChange })} data-testid="date-picker-input" />
            </FormItem>

            <div className={styles.time_group}>
              <FormItem error={errorMessageFor(errors, 'start')} className={styles.input_field}>
                <label className={styles.sr_only}>{t('Start Time')}</label>
                <TimeRangeInput
                  {...register('start', { onChange: onStartTimeChange })}
                  control={control}
                  timeZone={watchTimeZone}
                  dateList={dateList}
                  customStartTime={null}
                  placeholder={t('Start')}
                  data-testid="time-select-start"
                />
              </FormItem>

              <div className={styles.time_separator} />

              <FormItem error={errorMessageFor(errors, 'end')} className={styles.input_field}>
                <label className={styles.sr_only}>{t('End Time')}</label>
                <TimeRangeInput
                  {...register('end', { onChange: onEndTimeChange })}
                  control={control}
                  timeZone={watchTimeZone}
                  dateList={dateList}
                  customStartTime={watchStartTime}
                  placeholder={t('End')}
                  data-testid="time-select-end"
                />
              </FormItem>
            </div>
          </div>

          <div className={styles.sub_row}>
            <RecurrenceSelector compact />
          </div>
        </div>
      </div>

      {/* Notes row */}
      <div className={styles.icon_row}>
        <img src={EditPenIcon} alt="" className={styles.row_icon} />
        <div className={styles.row_content}>
          <FormItem error={errorMessageFor(errors, 'notes')} className={`${styles.input_field} ${styles.notes_field}`}>
            <label className={styles.sr_only}>{t('Notes')}</label>
            <textarea
              style={{ resize: 'none' }}
              {...register('notes')}
              placeholder={t('Add note here...')}
              data-testid="unavailability-notes-textarea"
            />
          </FormItem>
        </div>
      </div>
    </div>
  );
}
